import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
/* -----------------Child components--------------- */
import ProgressCardsContainer from "./ProgressCardsContainer";

/* -----------------Types--------------- */
import type { MasteryTagProgressData } from "@helpers/content/types";
import type { ExamCardData, SummaryCardData } from "@helpers/progress-data";
import { useAppStore } from "@hooks/useAppStore";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import Box from "@memorang/ui/src/components/Box";
import { ProgressSummaryCard } from "@memorang/ui/src/components/Cards/ProgressSummaryCard";
import { StudySessionMasteryTagProgressCard } from "@memorang/ui/src/components/Cards/StudySessionMasteryTagProgressCard";
import { router } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PracticeTestsCard } from "./PracticeTestsCard";

interface Props {
	masteryTagsProgressData: MasteryTagProgressData[];
	needsImprovementProgressData: MasteryTagProgressData | null;
	summaryCardData?: SummaryCardData;
	practiceTestsCardData?: ExamCardData;
	showStudyPackViews?: boolean;
}
const ProgressView = ({
	masteryTagsProgressData,
	needsImprovementProgressData,
	summaryCardData,
	showStudyPackViews,
	practiceTestsCardData,
}: Props) => {
	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);

	const showPracticeTests = useAppStore((state) => state.app.showPracticeTests);

	const { t } = useTranslation();
	const { handleSessionConfigNavigation } = useCustomNavigation();
	const weakTagIds = needsImprovementProgressData
		? needsImprovementProgressData.items.map((item) => item.id!)
		: [];
	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog(!showSessionConfigDialog);
	};

	const handleClickCard = (title: string) => {
		router.push({
			pathname: "/progress/detailed-list/mastery",
			params: {
				type: title,
			},
		});
	};
	const handleClickStudy = () => {
		const tagIds = needsImprovementProgressData
			? needsImprovementProgressData.items.map((item) => item.id!)
			: [];
		handleSessionConfigNavigation(toggleSessionConfigDialog, {
			selectedTagIds: tagIds,
			reset: "true",
		});
	};

	const masteryTagsCards = masteryTagsProgressData.map((item) => {
		return (
			<StudySessionMasteryTagProgressCard
				handleClickCard={() => handleClickCard(item.tagType!)}
				key={item.title}
				{...item}
			/>
		);
	});

	const cards = [
		...(practiceTestsCardData && showPracticeTests
			? [
					<PracticeTestsCard
						{...practiceTestsCardData}
						key="practice-tests-card"
					/>,
				]
			: []),
		...(summaryCardData
			? [<ProgressSummaryCard {...summaryCardData} key="summary-card" />]
			: []),
		<Box gap={16} key="mastery-tags-cards">
			{masteryTagsCards}
		</Box>,
		...(needsImprovementProgressData
			? [
					<StudySessionMasteryTagProgressCard
						handleClickCard={showStudyPackViews ? handleClickStudy : undefined}
						{...needsImprovementProgressData}
						cta={
							showStudyPackViews
								? t("progress.needsImprovement.cta")
								: undefined
						}
						key="needs-improvement-card"
					/>,
				]
			: []),
	];
	return (
		<Box gap={16}>
			<ProgressCardsContainer cards={cards} />
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					selectedTagIds={weakTagIds}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
		</Box>
	);
};

export default ProgressView;
